/* Activate desk v3 — stencil bench · Live SN/QR identify · flow motion · #/activate only
 * Honesty: Take custody · Open machine · Need SN
 * Not painted: Identify with SN or QR to stamp this plate · Plate · Device · Factory · yes · no MAC · FW
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { AnimatePresence, LayoutGroup } from "framer-motion";
import { html, M, F, Ico, Field, Input, Alert, SOFT, go, stay } from "./ui.js";
import { useApi, useAction, path } from "./api.js";
import { boardLoopViewOf, edgeWaveHonestyOf } from "./edgeos-live.js";
import { safeBoardPath } from "./board-api.js";
import { gnssSealSay, postDevicePositionSeal } from "./operator-gnss-seal.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { setDirtySeat } from "./quality-runtime.js";
import { isLocalHost } from "./pathseal.js";
import {
  DEVICE_ID_RE,
  FACTORY_SN_RE,
  activateSeatOf,
  activateMachineIdOf,
  claimableIdentifyOf,
  identifyStatusTone,
  safeActivatePath,
  softActivateErrorOf,
  fmtMac,
  liveClaimDeviceIdOf,
  pendingIdentifyBodyOf,
  hintsOf,
  normalizeQrPayload,
} from "./activate-shared.js";
import { boardHeadingOf, looksLikeCode } from "./operator-say.js";

const PHASES = [
  { id: "find", label: "Find", say: "Serial or QR", purpose: "Scan or type the serial. Waiting boards appear here." },
  { id: "claim", label: "Claim", say: "Bind to this site", purpose: "Bind this board to a zone on your site." },
  { id: "finish", label: "Finish", say: "Wi‑Fi · open the board", purpose: "Send Wi‑Fi and open the board." },
];

const SN_RE = FACTORY_SN_RE;

const ACT3_SPRING = { type: "spring", stiffness: 480, damping: 28, mass: 0.62 };
const ACT3_TAP = { scale: 0.94 };
const ACT3_HOVER = { y: -3, scale: 1.04 };
const ACT3_HOVER_SUBTLE = { y: -2, scale: 1.02 };
const ACT3_PANE_ENTER = { opacity: 0, x: 28, filter: "blur(6px)" };
const ACT3_PANE_EXIT = { opacity: 0, x: -20, filter: "blur(4px)" };

/** Local-only: unlock all phases + fixture board for UI review. ?activate_unlock=1&activate_step=claim|finish */
const ACT3_PREVIEW_IDENTIFY = {
  status: "ready",
  serial: "SN-PREVIEW00001",
  mac_nocolon: "AABBCCDDEEFF",
  fw: "preview-fw",
  last_seen_at: "2026-08-27T00:00:00Z",
  message: "Local preview fixture — not Live.",
};
const ACT3_PREVIEW_CLAIM = {
  ok: true,
  device_id: "SN-PREVIEW00001",
  cred_version: 1,
  lifecycle_state: "provisioned",
  owner_admin: "preview@local",
  bootstrap_publish_ok: true,
};

function activateLocalUnlock() {
  try {
    if (!isLocalHost()) return false;
    const q = String(location.search || "");
    if (/(?:\?|&)activate_unlock=1(?:&|$)/.test(q)) return true;
    if (/(?:\?|&)activate_step=/.test(q)) return true;
    return typeof sessionStorage !== "undefined"
      && sessionStorage.getItem("croc.activate.unlock") === "1";
  } catch {
    return false;
  }
}

function activateLocalStepFromQuery() {
  try {
    const m = String(location.search || "").match(/(?:\?|&)activate_step=(\w+)/i);
    if (!m) return 0;
    const v = m[1].toLowerCase();
    if (v === "find" || v === "0") return 0;
    if (v === "claim" || v === "1") return 1;
    if (v === "finish" || v === "2") return 2;
    const n = parseInt(v, 10);
    return (n >= 0 && n <= 2) ? n : 0;
  } catch {
    return 0;
  }
}

function formatAct3Status(st) {
  const s = String(st || "").toLowerCase();
  const map = {
    ready: "Ready",
    ready_factory: "Ready on the factory list",
    offline: "Offline",
    already_registered: "Already on a site",
    unknown_serial: "Unknown serial",
    blocked: "Blocked",
    awaiting: "Waiting",
    claimed: "Claimed",
  };
  return map[s] || String(st || "—");
}

async function copyAct3Text(text, toast, label) {
  const t = String(text || "").trim();
  if (!t || t === "—") return;
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(t);
      toast && toast((label ? label + " · " : "") + "Copied", "go");
    }
  } catch {
    toast && toast("Clipboard unavailable", "warn");
  }
}

function CopyDd({ value, label, toast, warn }) {
  const v = String(value || "").trim();
  if (!v || v === "—") {
    return html`<dd data-empty="1">—</dd>`;
  }
  return html`<dd className=${warn ? "act3__warn act3__copy-dd" : "act3__copy-dd"}
    title="Click to copy"
    onClick=${() => copyAct3Text(v, toast, label)}>${v}</dd>`;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    try {
      const mq = matchMedia("(prefers-reduced-motion: reduce)");
      const sync = () => setReduced(!!mq.matches);
      sync();
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    } catch {
      return undefined;
    }
  }, []);
  return reduced;
}

function pointerGlow(e) {
  const t = e.currentTarget;
  if (!t || !t.getBoundingClientRect) return;
  const r = t.getBoundingClientRect();
  t.style.setProperty("--act3-px", ((e.clientX - r.left) / r.width * 100) + "%");
  t.style.setProperty("--act3-py", ((e.clientY - r.top) / r.height * 100) + "%");
}

/** Motion button — hover lift, tap squash, cursor-follow glow. */
function Act3Btn({
  variant = "ghost",
  className = "",
  disabled,
  onClick,
  children,
  hover = ACT3_HOVER,
  ...rest
}) {
  const base = variant === "go"
    ? "btn btn--go act3__btn act3__btn--go"
    : variant === "sm-go"
      ? "btn btn--go btn--sm act3__btn act3__btn--go"
      : variant === "sm"
        ? "btn btn--ghost btn--sm act3__btn"
        : "btn btn--ghost act3__btn";
  const cls = className ? (className + " act3__btn") : base;
  return html`
    <${M.button}
      type="button"
      className=${cls}
      disabled=${disabled}
      onClick=${onClick}
      onPointerMove=${pointerGlow}
      onPointerLeave=${(e) => {
        e.currentTarget.style.removeProperty("--act3-px");
        e.currentTarget.style.removeProperty("--act3-py");
      }}
      whileHover=${disabled ? undefined : hover}
      whileTap=${disabled ? undefined : ACT3_TAP}
      transition=${ACT3_SPRING}
      ...${rest}>
      <span className="act3__btn-sheen" aria-hidden="true"/>
      <span className="act3__btn-label">${children}</span>
    </${M.button}>`;
}

/** Ambient canvas particles — mouse repel + phase tint. */
function Act3ParticleLayer({ step, reduced }) {
  const hostRef = useRef(null);
  const rafRef = useRef(0);
  const stateRef = useRef({ pts: [], w: 0, h: 0, mx: 0.5, my: 0.25, step: 0 });

  useEffect(() => {
    stateRef.current.step = step;
  }, [step]);

  useEffect(() => {
    if (reduced) return undefined;
    const host = hostRef.current;
    if (!host) return undefined;
    const canvas = document.createElement("canvas");
    host.appendChild(canvas);
    const ctx = canvas.getContext("2d", { alpha: true });

    const palette = [
      [61, 225, 200],
      [201, 166, 107],
      [61, 225, 200],
    ];

    const resize = () => {
      const rect = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, rect.width);
      const h = Math.max(1, rect.height);
      stateRef.current.w = w;
      stateRef.current.h = h;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.max(48, Math.min(80, Math.floor((w * h) / 11000)));
      stateRef.current.pts = Array.from({ length: n }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.65,
        vy: (Math.random() - 0.5) * 0.65,
        r: 1.1 + Math.random() * 2.4,
        tw: Math.random() * Math.PI * 2,
      }));
    };

    const onMove = (e) => {
      const rect = host.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      stateRef.current.mx = (e.clientX - rect.left) / rect.width;
      stateRef.current.my = (e.clientY - rect.top) / rect.height;
    };

    const tick = () => {
      const st = stateRef.current;
      const { w, h, pts, mx, my } = st;
      if (!w || !h || !pts.length) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const [cr, cg, cb] = palette[st.step] || palette[0];
      ctx.clearRect(0, 0, w, h);
      const mxp = mx * w;
      const myp = my * h;
      for (const p of pts) {
        const dx = mxp - p.x;
        const dy = myp - p.y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < 200) {
          const f = (200 - dist) / 200;
          p.vx -= (dx / dist) * f * 0.07;
          p.vy -= (dy / dist) * f * 0.07;
        }
        p.vx += (Math.random() - 0.5) * 0.006;
        p.vy += (Math.random() - 0.5) * 0.006;
        p.vx *= 0.986;
        p.vy *= 0.986;
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -4) p.x = w + 4;
        if (p.x > w + 4) p.x = -4;
        if (p.y < -4) p.y = h + 4;
        if (p.y > h + 4) p.y = -4;
        p.tw += 0.022;
        const a = 0.2 + Math.sin(p.tw) * 0.14;
        ctx.beginPath();
        ctx.fillStyle = "rgba(" + cr + "," + cg + "," + cb + "," + a + ")";
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 100) {
            ctx.strokeStyle = "rgba(" + cr + "," + cg + "," + cb + "," + (0.14 * (1 - d / 100)) + ")";
            ctx.lineWidth = 0.85;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      ctx.beginPath();
      const grd = ctx.createRadialGradient(mxp, myp, 0, mxp, myp, 150);
      grd.addColorStop(0, "rgba(" + cr + "," + cg + "," + cb + ",0.16)");
      grd.addColorStop(1, "rgba(" + cr + "," + cg + "," + cb + ",0)");
      ctx.fillStyle = grd;
      ctx.arc(mxp, myp, 150, 0, Math.PI * 2);
      ctx.fill();
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(host);
    else window.addEventListener("resize", resize);
    const moveRoot = host.closest(".sheet.activate-v3") || window;
    moveRoot.addEventListener("pointermove", onMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", resize);
      moveRoot.removeEventListener("pointermove", onMove);
      canvas.remove();
    };
  }, [reduced]);

  if (reduced) return null;
  return html`<div ref=${hostRef} className="act3__particles" aria-hidden="true"/>`;
}

function maxStepOf({ identify, claimOut }) {
  const claimed = !!(claimOut && (claimOut.ok || claimOut.device_id || claimOut.lifecycle_state));
  if (claimed) return 2;
  if (claimableIdentifyOf(identify)) return 1;
  return 0;
}

function wifiLabel(st) {
  if (st === "running") return "Waiting ack";
  if (st === "success") return "Saved";
  if (st === "failed") return "Rejected";
  return st || "—";
}

function identifyBodyOf(raw) {
  const s = String(raw || "").trim();
  if (!s) return { error: "Type the sticker serial, or paste the full QR from the board." };
  const up = s.toUpperCase();
  if (up.includes("|") || up.startsWith("CROC|")) return { body: { qr_code: s } };
  if (SN_RE.test(up)) return { body: { serial: up } };
  const hex = up.replace(/[^0-9A-F]/g, "");
  if (hex.length === 12 || up.startsWith("CROC-")) {
    return {
      error: "Use the sticker serial or the full QR. The short network code alone will not work.",
    };
  }
  return { error: "The sticker serial looks like SN- followed by 16 characters." };
}

function SeatChips({ seat }) {
  const chips = [
    ["Find", !!(seat && seat.queueAllowed)],
    ["Claim", !!(seat && seat.custodyAllowed)],
    ["Finish", !!(seat && seat.wireAllowed)],
  ];
  return html`<div className="act3__seats" aria-label="Activate seats">
    ${chips.map(([label, on]) => html`
      <span key=${label} className="act3__seat" data-on=${on ? "1" : "0"}>${label}</span>`)}
  </div>`;
}

function StencilPlate({ identify, claimOut, deviceId, zone, toast }) {
  const macRaw = String((identify && identify.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "");
  const mac = macRaw.length === 12 ? fmtMac(macRaw) : "—";
  const serial = (identify && identify.serial) || "—";
  const hwRaw = (claimOut && claimOut.device_id)
    || liveClaimDeviceIdOf(identify, deviceId)
    || "—";
  const hw = looksLikeCode(hwRaw) ? boardHeadingOf(hwRaw) : hwRaw;
  const status = (claimOut && (claimOut.lifecycle_state || claimOut.device_id && "claimed"))
    || (identify && identify.status)
    || "awaiting";
  const tone = claimOut ? "go" : (claimableIdentifyOf(identify) ? "ready" : identify ? "warn" : "idle");
  const fw = identify && identify.fw;
  const seen = identify && identify.last_seen_at;
  /* Live: claimed_at only on already_registered identify — claim success has no claimed_at. */
  const claimedAt = (identify && identify.status === "already_registered" && identify.claimed_at)
    ? identify.claimed_at
    : null;
  const hints = hintsOf(identify);
  const stamped = !!(identify || claimOut);
  const macHint = identify && identify.mac_hint;
  const factoryReg = identify && identify.factory_registered === true;

  return html`
    <aside className="act3__stencil" data-tone=${tone} data-stamped=${stamped ? "1" : "0"}
      data-hints=${hints.length > 0 ? "1" : "0"} aria-label="Board plate">
      <${M.div} className="act3__stencil-inner" key=${status + String(mac || "") + String(hw)}
        initial=${stamped ? { opacity: 0, y: 14, scale: 0.97 } : false}
        animate=${{ opacity: 1, y: 0, scale: 1 }}
        transition=${ACT3_SPRING}>
        <header>
          <small>This board</small>
          <b className="act3__status-pill" data-status=${tone}>${formatAct3Status(status)}</b>
        </header>
        ${!stamped ? html`
          <div className="act3__stencil-ghost" aria-hidden="true">
            <span/><span/><span/>
          </div>` : html`<dl>
          ${[
            ["Serial", serial],
            ["Board", hw],
            ["Address", mac],
            ["Zone", zone || "all"],
            fw ? ["Software", fw] : null,
            seen ? ["Last seen", seen] : null,
            claimedAt ? ["Claimed", claimedAt] : null,
            (claimOut && claimOut.cred_version != null) ? ["Credential", "v" + claimOut.cred_version] : null,
            (claimOut && claimOut.lifecycle_version != null) ? ["Board version", String(claimOut.lifecycle_version)] : null,
            (claimOut && claimOut.owner_admin) ? ["Owner", claimOut.owner_admin] : null,
            (identify && identify.owner_admin && identify.status === "already_registered")
              ? ["Owner", identify.owner_admin] : null,
            (claimOut && claimOut.bootstrap_publish_ok === false) ? ["Wi‑Fi setup", "waiting"] : null,
            (claimOut && claimOut.bootstrap_publish_ok === true) ? ["Wi‑Fi setup", "queued"] : null,
            factoryReg && !macRaw ? ["On factory list", "on the list · not seen on the network yet"] : null,
            macHint ? ["Hint", String(macHint)] : null,
          ].filter(Boolean).map(([k, v]) => html`
            <div key=${k} data-empty=${(!v || v === "—") ? "1" : "0"}>
              <dt>${k}</dt>
              ${(k === "Serial" || k === "Board" || k === "Address")
                ? html`<${CopyDd} value=${v} label=${k} toast=${toast}
                  warn=${k === "Bootstrap" && v === "deferred"}/>`
                : html`<dd className=${k === "Bootstrap" && v === "deferred" ? "act3__warn" : ""}>${v}</dd>`}
            </div>`)}
        </dl>`}
        ${stamped && identify && identify.message
          ? html`<p className="act3__stencil-msg">${identify.message}</p>`
          : !stamped
            ? html`<p className="act3__stencil-msg act3__stencil-msg--ghost">Scan the sticker or the full QR to name this board</p>`
            : null}
        ${stamped && hints.length > 0 && html`
          <ol className="act3__hints act3__hints--plate">
            ${hints.map((h, i) => html`<li key=${i}>${h}</li>`)}
          </ol>`}
        ${!stamped && html`<div className="act3__ghost-mark" aria-hidden="true"/>`}
      <//>
    </aside>`;
}

export default function ActivateDeskV3({ toast }) {
  const { me } = useSession();
  const reduceMotion = useReducedMotion();
  const localUnlock = activateLocalUnlock();
  const localStep = localUnlock ? activateLocalStepFromQuery() : 0;
  const role = String((me && me.role) || "").toLowerCase();
  const posture = useApi(safeActivatePath("get__activate_status"));
  const post = posture.data || {};
  const seat = posture.loading
    ? activateSeatOf({ enabled: false, principal_role: role, limits: { pending_paint_max: 48 } })
    : activateSeatOf(post);

  const [at, setAt] = useState(localStep);
  const [serial, setSerial] = useState(localUnlock ? ACT3_PREVIEW_IDENTIFY.serial : "");
  const [deviceId, setDeviceId] = useState(localUnlock ? ACT3_PREVIEW_IDENTIFY.serial : "");
  const [zone, setZone] = useState("all");
  const [identify, setIdentify] = useState(localUnlock ? ACT3_PREVIEW_IDENTIFY : null);
  const [claimOut, setClaimOut] = useState(
    localUnlock && localStep >= 2 ? ACT3_PREVIEW_CLAIM : null,
  );

  const maxStep = localUnlock ? 2 : maxStepOf({ identify, claimOut });
  const machineId = activateMachineIdOf(identify, claimOut, deviceId);
  const resetSession = () => {
    setAt(0);
    setSerial("");
    setDeviceId("");
    setZone("all");
    setIdentify(null);
    setClaimOut(null);
  };
  const goStep = (i) => {
    if (i < 0 || i >= PHASES.length) return;
    if (!localUnlock && i > maxStep) {
      toast && toast("Finish the earlier phase first", "warn");
      return;
    }
    setAt(i);
  };
  const step = {
    serial, setSerial, deviceId, setDeviceId, zone, setZone,
    identify, setIdentify, claimOut, setClaimOut, toast, seat, posture, post, goStep,
    resetSession, machineId, reduceMotion,
  };
  const PANE = [FindPane, ClaimPane, FinishPane][at];

  useEffect(() => {
    try {
      const h = String(location.hash || "");
      if (h.startsWith("#/activate/") && h !== "#/activate") stay("/activate");
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    const dirty = !!(String(serial || "").trim() || String(deviceId || "").trim());
    setDirtySeat(dirty);
    return () => { try { setDirtySeat(false); } catch (_) { /* ignore */ } };
  }, [serial, deviceId]);

  /* No auto-force maxStep→at: operators may re-open Find after a claimable stamp. */

  const reReadPosture = () => {
    if (posture.loading) {
      toast && toast("Checking access…", "warn");
      return;
    }
    if (!seat.reReadAllowed && !posture.error) {
      toast && toast("Re-read is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    toast && toast("Access re-checked", "go");
  };

  return html`
    <div className="sheet act-desk act-desk--liquid desk-craft activate-v3"
      data-cmc-page="page95-activate" data-cmc-activate="page17"
      data-cmc-residual="page214-activate" data-cmc-shell="activate-ux"
      data-cmc-activate-ux="activate-v3-stencil"
      data-act3-flat="1"
      data-act3-preview=${localUnlock ? "1" : "0"}
      data-act3-step=${at}
      data-act3-motion=${reduceMotion ? "reduced" : "on"}>
      <div className="act3__fx" aria-hidden="true">
        <${Act3ParticleLayer} step=${at} reduced=${reduceMotion}/>
        <div className="act3__trace"/>
      </div>
      <div className="wrap act3__shell">
        <div className="act3__band">
          <div className="act3__rail">
            <${LayoutGroup}>
              <nav className="act3__orbit" aria-label="Phases">
                ${PHASES.map((ph, i) => html`<${M.button} key=${ph.id} type="button" className="act3__phase"
                  data-on=${i === at ? "1" : "0"} data-done=${i < at ? "1" : "0"}                   data-locked=${!localUnlock && i > maxStep ? "1" : "0"}
                  disabled=${!localUnlock && i > maxStep}
                  aria-label=${ph.label + " — " + ph.say}
                  aria-current=${i === at ? "step" : undefined}
                  onClick=${() => goStep(i)}
                  onPointerMove=${pointerGlow}
                  whileHover=${(!localUnlock && i > maxStep) ? undefined : ACT3_HOVER_SUBTLE}
                  whileTap=${(!localUnlock && i > maxStep) ? undefined : { scale: 0.98 }}
                  transition=${ACT3_SPRING}>
                  ${i === at && html`<${M.span} className="act3__phase-glow" layoutId="act3-phase-glow"
                    transition=${ACT3_SPRING} aria-hidden="true"/>`}
                  ${i < at && html`<span className="act3__phase-done" aria-hidden="true"><${Ico} d="check" w=${12}/></span>`}
                  <span className="act3__phase-label">${ph.label}</span>
                <//>`)}
              </nav>
            <//>
            <div className="act3__rail-meta">
              <${AnimatePresence} mode="wait">
                <${M.p} key=${PHASES[at].id} className="act3__rail-caption"
                  initial=${reduceMotion ? false : { opacity: 0, y: 4 }}
                  animate=${{ opacity: 1, y: 0 }}
                  exit=${reduceMotion ? undefined : { opacity: 0, y: -3 }}
                  transition=${SOFT}>
                  ${PHASES[at].say}
                <//>
              <//>
              <${SeatChips} seat=${seat}/>
            </div>
          </div>
          <div className="act3__band-tools">
            <${Act3Btn} className="btn btn--ghost btn--sm act3__tool" hover=${ACT3_HOVER_SUBTLE}
              disabled=${posture.loading || (!seat.reReadAllowed && !posture.error)}
              onClick=${reReadPosture} title="Refresh access" aria-label="Refresh access">
              <${Ico} d="refresh" w=${13}/></${Act3Btn}>
            <${Act3Btn} className="btn btn--ghost btn--sm act3__tool" hover=${ACT3_HOVER_SUBTLE}
              onClick=${() => go("/machine")}>Machine</${Act3Btn}>
          </div>
        </div>

        <div className="act3__posture">
          ${localUnlock && html`<${Alert} tone="info" title="Local preview"
            say="Preview unlocked for this browser only."/>`}
          ${posture.loading ? html`<${Alert} tone="info" title="Checking access" say="Loading seats…"/>`
          : posture.error ? html`<${Alert} tone="warn" title="Activate unavailable"
            say=${/\b404\b|not found/i.test(String(posture.error || ""))
              ? "Activate is not available on this server right now."
              : softActivateErrorOf(posture.error)}/>`
          : (!seat.queueAllowed || !seat.custodyAllowed) ? html`<${Alert} tone="warn"
            title="Activation partly closed"
            say=${seat.detail === "activate_capability_denied"
              ? "This seat cannot take a board. Ask a manager to allow it."
              : !seat.queueAllowed
                ? (softActivateErrorOf(seat.detail) || "Find is closed for this seat.")
                : "Taking a board is closed for this seat."}/>` : null}
        </div>

        <div className="act3__grid">
          <${AnimatePresence} mode="wait">
            <${M.div} key=${PHASES[at].id} className="act3__work"
              initial=${reduceMotion ? false : ACT3_PANE_ENTER}
              animate=${{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit=${reduceMotion ? undefined : ACT3_PANE_EXIT}
              transition=${{ type: "spring", stiffness: 380, damping: 30, mass: 0.72 }}>
              <${PANE} ...${step}/>
            <//>
          <//>
          <${StencilPlate} identify=${identify} claimOut=${claimOut} deviceId=${deviceId} zone=${zone} toast=${toast}/>
          <div className="act3__live a11y" aria-live="polite" aria-atomic="true">
            ${identify ? formatAct3Status(identify.status) + (identify.message ? " — " + identify.message : "") : ""}
          </div>
        </div>

        <footer className="act3__dock">
          <span className="act3__dock-step">${at + 1} / ${PHASES.length}</span>
          ${at > 0 && html`<${Act3Btn} variant="ghost" hover=${ACT3_HOVER_SUBTLE}
            onClick=${() => goStep(at - 1)}>
            <${Ico} d="arrowL" w=${14}/> ${PHASES[at - 1].label}</${Act3Btn}>`}
          <span className="act3__dock-flow" aria-hidden="true">
            <${M.i} className="act3__dock-bar"
              animate=${{ scaleX: (at + 1) / PHASES.length }}
              style=${{ originX: 0 }} transition=${ACT3_SPRING}/>
            <i className="act3__dock-shimmer"/>
          </span>
          ${at < PHASES.length - 1 && html`<${Act3Btn}
            variant=${(!localUnlock && (at + 1) > maxStep) ? "ghost" : "go"}
            className=${(!localUnlock && (at + 1) > maxStep) ? "btn btn--ghost" : "btn btn--go"}
            disabled=${!localUnlock && (at + 1) > maxStep}
            title=${(!localUnlock && (at + 1) > maxStep) ? "Finish this phase first" : ("Go to " + PHASES[at + 1].label)}
            onClick=${() => goStep(at + 1)}>
            ${PHASES[at + 1].label} <${Ico} d="arrowR" w=${14}/></${Act3Btn}>`}
          ${at === PHASES.length - 1 && html`<${Act3Btn} variant="go"
            disabled=${!machineId}
            onClick=${() => {
              if (!machineId) { toast && toast("Claim a device first", "warn"); return; }
              go("/machine/" + encodeURIComponent(machineId));
            }}>
          Open this board <${Ico} d="arrowR" w=${14}/></${Act3Btn}>`}
        </footer>
      </div>
    </div>`;
}

/* ── Find ── */
function OpSteps({ identify, title }) {
  const hints = hintsOf(identify);
  if (!hints.length) return null;
  return html`
    <div className="opsteps act3__opsteps">
      <u>${title || "Tell the person holding the board"}</u>
      ${hints.map((h, i) => html`<div key=${i} className="opstep"><b>${i + 1}</b><span>${h}</span></div>`)}
    </div>`;
}

function FindPane({ serial, setSerial, identify, setIdentify, setDeviceId, toast, seat, goStep, reduceMotion }) {
  const { run, busy, error, errorOf, envelope } = useAction();
  const queueOn = !!(seat && seat.queueAllowed);
  const pendingOn = !!(seat && seat.pendingAllowed === true);
  const pending = useApi(pendingOn ? safeActivatePath("get__provision_pending") : null);
  const allRows = (pending.data && (pending.data.items || pending.data.pending)) || [];
  const paintMax = (seat && seat.pendingPaintMax) || 48;
  const rows = Array.isArray(allRows) ? allRows.slice(0, paintMax) : [];
  const claimable = claimableIdentifyOf(identify);
  const status = identify && identify.status;
  const registeredId = identify && identify.status === "already_registered" && identify.device_id;
  const [camOn, setCamOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanLoopRef = useRef(0);

  const stopCamera = () => {
    scanLoopRef.current += 1;
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
    }
    const v = videoRef.current;
    if (v) {
      try { v.srcObject = null; } catch (_) { /* ignore */ }
    }
    setCamOn(false);
  };

  useEffect(() => () => { stopCamera(); }, []);

  useEffect(() => {
    const el = document.querySelector(".act3__find .act3__serial-inp input");
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch { /* ignore */ }
  }, []);

  const activeMac = String((identify && identify.mac_nocolon) || "")
    .replace(/[^0-9A-Fa-f]/g, "").toUpperCase();

  const applyQr = async (raw, how) => {
    const t = normalizeQrPayload(raw);
    if (!t) {
      toast && toast("No QR text — paste into the field", "warn");
      return false;
    }
    setSerial(t);
    const parsed = identifyBodyOf(t);
    if (parsed.error) {
      toast && toast("QR loaded · " + how + " — " + parsed.error, "warn");
      return true;
    }
    toast && toast("QR loaded · identifying…", "go");
    await submitIdentify(parsed);
    return true;
  };

  const pasteQrLive = async () => {
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.readText !== "function") {
        toast && toast("Paste into the field (Ctrl+V)", "warn");
        return;
      }
      const t = await navigator.clipboard.readText();
      applyQr(t, "clipboard");
    } catch (_) {
      toast && toast("Clipboard denied — paste manually", "warn");
    }
  };

  const startCameraQr = async () => {
    if (typeof window.BarcodeDetector !== "function") {
      toast && toast("Camera QR needs Chrome or Edge", "warn");
      return;
    }
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
      toast && toast("Camera unavailable", "warn");
      return;
    }
    stopCamera();
    try {
      detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
    } catch (_) {
      try { detectorRef.current = new window.BarcodeDetector(); }
      catch {
        toast && toast("BarcodeDetector refused", "warn");
        return;
      }
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
    } catch (_) {
      toast && toast("Camera permission denied", "warn");
      return;
    }
    streamRef.current = stream;
    setCamOn(true);
    const token = ++scanLoopRef.current;
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (!v || token !== scanLoopRef.current) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
        return;
      }
      v.srcObject = stream;
      v.play().catch(() => {});
      const tick = async () => {
        if (token !== scanLoopRef.current) return;
        const video = videoRef.current;
        const det = detectorRef.current;
        if (video && det && video.readyState >= 2) {
          try {
            const codes = await det.detect(video);
            if (codes && codes.length) {
              const raw = codes[0].rawValue || codes[0].rawData || "";
              if (applyQr(raw, "camera")) {
                stopCamera();
                return;
              }
            }
          } catch (_) { /* keep scanning */ }
        }
        if (token === scanLoopRef.current) setTimeout(tick, 280);
      };
      setTimeout(tick, 320);
    });
  };

  const applyIdentifyOut = (out) => {
    setIdentify(out);
    const nextId = liveClaimDeviceIdOf(out, "");
    if (nextId) setDeviceId(nextId);
    const st = String(out.status || "");
    toast && toast(st + (out.message ? " · " + String(out.message).slice(0, 72) : ""),
      claimableIdentifyOf(out) ? "go" : identifyStatusTone(st) === "bad" ? "bad" : "warn");
    if (claimableIdentifyOf(out)) goStep(1);
  };

  const submitIdentify = async (parsed) => {
    if (!queueOn || !(seat && seat.identifyAllowed)) {
      toast && toast("Identify is closed for this seat", "warn");
      return null;
    }
    if (parsed.error) {
      toast && toast(parsed.error, "warn");
      return null;
    }
    const out = await run(safeActivatePath("post__provision_identify"), { body: parsed.body });
    if (!out) {
      toast && toast(softActivateErrorOf(errorOf()) || "Could not identify this board", "bad");
      return null;
    }
    applyIdentifyOut(out);
    return out;
  };

  const runIdentify = async () => {
    await submitIdentify(identifyBodyOf(serial));
  };

  const pickPending = async (r) => {
    if (!queueOn || !(seat && seat.identifyAllowed) || !pendingOn) {
      toast && toast("Queue is closed for this seat", "warn");
      return;
    }
    const parsed = pendingIdentifyBodyOf(r);
    if (parsed.error) {
      const snHint = FACTORY_SN_RE.test(String(r.proposed_device_id || ""))
        ? String(r.proposed_device_id).toUpperCase()
        : "";
      if (snHint) setSerial(snHint);
      toast && toast(parsed.error, "warn");
      return;
    }
    setIdentify(null);
    const out = await run(safeActivatePath("post__provision_identify"), { body: parsed.body });
    if (!out) {
      toast && toast(softActivateErrorOf(errorOf()) || "Could not identify this board", "bad");
      return;
    }
    applyIdentifyOut(out);
  };

  const rowCanIdentify = (r) => !pendingIdentifyBodyOf(r).error;

  return html`
    <div className="act3__stack">
      <section className="act3__pane act3__find">
        <header className="act3__sec-head">
          <span>Identify</span>
          <em>Sticker or QR</em>
        </header>
      ${envelope
        ? html`<p className="act3__hint-row" data-cmc-last-action="1">Last action · ${envelopeSummary(envelope) || "—"}</p>`
        : null}
      <${Field} hint="Use the sticker serial or the full QR. The short network code alone will not work.">
        <${Input} icon="chip" value=${serial} className="act3__serial-inp"
          placeholder="SN-…"
          autoComplete="off" spellCheck=${false}
          onChange=${(e) => setSerial(e.target.value)}
          onBlur=${(e) => {
            const v = String(e.target.value || "").trim();
            if (v && !v.includes("|")) setSerial(v.toUpperCase());
          }}
          onKeyDown=${(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              runIdentify();
            }
          }}
          act="Paste QR" onAct=${pasteQrLive}/>
      <//>
      <p className="act3__hint-row">
        <kbd className="act3__kbd">Enter</kbd> to Identify
        <span className="act3__hint-dot" aria-hidden="true">·</span>
        paste a QR to start
      </p>
      <div className="act3__acts">
        <${Act3Btn} variant="go"
          disabled=${busy || !queueOn || !(seat && seat.identifyAllowed)}
          onClick=${runIdentify}>
          ${busy ? "Identifying…" : "Identify"}</${Act3Btn}>
        <${Act3Btn} hover=${ACT3_HOVER_SUBTLE}
          onClick=${camOn ? stopCamera : startCameraQr}>
          ${camOn ? "Stop camera" : "Scan QR"}</${Act3Btn}>
        ${claimable && html`<${Act3Btn} hover=${ACT3_HOVER_SUBTLE} onClick=${() => goStep(1)}>Continue</${Act3Btn}>`}
      </div>
      ${identify && html`<${M.div} className="act3__pulse" data-tone=${identifyStatusTone(status)}
        initial=${reduceMotion ? false : { opacity: 0, y: 8, scale: 0.98 }}
        animate=${{ opacity: 1, y: 0, scale: 1 }}
        transition=${ACT3_SPRING}>
        <b>${formatAct3Status(status)}</b>
        <span>${identify.message || "Live identify result."}</span>
      <//>`}
      ${status === "offline" && html`<${Alert} tone="warn" title="Cannot claim yet"
        say=${identify.message || "This board is on the factory list, but it has not checked in yet. Set up Wi‑Fi first."}/>`}
      ${status === "already_registered" && html`<${Alert} tone="warn" title="Already on a site"
        say=${identify.message || "This serial is already registered — open Machine instead of reclaiming."}/>`}
      ${(status === "blocked" || status === "unknown_serial") && html`<${Alert} tone="bad"
        title=${status === "blocked" ? "Factory blocked" : "Unknown serial"}
        say=${identify.message || "This board cannot be activated from here."}/>`}
      ${identify && hintsOf(identify).length > 0 && html`<${OpSteps} identify=${identify}/>`}
      ${registeredId && html`<div className="act3__acts">
        <${Act3Btn} variant="go"
          onClick=${() => go("/machine/" + encodeURIComponent(String(registeredId)))}>
          Open this board <${Ico} d="arrowR" w=${14}/></${Act3Btn}>
      </div>`}
      ${camOn ? html`
        <div className="act3__cam">
          <video ref=${videoRef} muted playsInline/>
        </div>` : null}
      ${error && html`<${Alert} tone="warn" title="Refused" say=${softActivateErrorOf(error)}/>`}
      </section>

      <section className="act3__pane act3__pane--queue">
        <header className="act3__sec-head">
          <span>Waiting queue</span>
          <div className="act3__sec-tools">
            <em className="act3__count">${pending.loading ? "…" : (pendingOn ? String(rows.length).padStart(2, "0") : "—")}</em>
            <${Act3Btn} className="btn btn--ghost btn--sm act3__tool act3__tool--icon" hover=${ACT3_HOVER_SUBTLE}
              disabled=${!pendingOn || !(seat && seat.reReadAllowed)}
              onClick=${() => pending.reload && pending.reload()} title="Refresh" aria-label="Refresh queue">
              <${Ico} d="refresh" w=${13}/></${Act3Btn}>
          </div>
        </header>
      ${!queueOn || !pendingOn
        ? html`<div className="act3__empty"><span>Queue closed for this seat</span></div>`
        : pending.error
          ? html`<${Alert} tone="warn" title="Queue error" say=${softActivateErrorOf(pending.error)}/>`
          : !rows.length
            ? html`<div className="act3__empty">
                <span>${pending.loading ? "Reading pending boards…" : "No board has checked in"}</span>
                <small>Type the sticker serial, or paste the QR from the board</small>
              </div>`
            : html`<div className="act3__ledger-wrap">
                <div className="act3__ledger-head" aria-hidden="true">
                  <span>Board</span><span>Last seen</span><span>Name</span><span/>
                </div>
                <${M.ul} className="act3__ledger" initial="hidden" animate="visible"
                variants=${{ visible: { transition: { staggerChildren: 0.04 } } }}>
                ${rows.map((r) => {
                  const can = rowCanIdentify(r);
                  const rowMac = String(r.mac_nocolon || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
                  const selected = !!(activeMac && rowMac && activeMac === rowMac);
                  return html`
                  <${M.li} key=${r.mac_nocolon || r.mac}
                    data-selected=${selected ? "1" : "0"}
                    variants=${{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0 } }}
                    whileHover=${reduceMotion ? undefined : { x: 4, backgroundColor: "rgba(61,225,200,.04)" }}
                    transition=${ACT3_SPRING}>
                    <code className="act3__ledger-mac">${fmtMac(r.mac_nocolon) || r.mac || "—"}</code>
                    <span title=${r.last_seen_at || ""}>${r.last_seen_at || r.fw || "—"}</span>
                    <code className="act3__ledger-id">${boardHeadingOf(r.proposed_device_id)}</code>
                    <${Act3Btn}
                      variant=${can ? "sm-go" : "sm"}
                      className=${can ? "btn btn--sm btn--go" : "btn btn--sm btn--ghost"}
                      hover=${ACT3_HOVER_SUBTLE}
                      disabled=${busy || !pendingOn || !(seat && seat.identifyAllowed)}
                      title=${can ? "Identify this waiting board" : "Needs the sticker serial or the full QR"}
                      onClick=${() => pickPending(r)}>${can ? "Select" : "Need sticker"}</${Act3Btn}>
                  <//>`;
                })}
              <//></div>`}
      </section>
    </div>`;
}

/* ── Claim (+ optional legacy challenge) ── */
function ClaimPane({ deviceId, setDeviceId, zone, setZone,
  identify, claimOut, setClaimOut, toast, seat, goStep, reduceMotion }) {
  const { run, busy, error, errorOf, envelope } = useAction();
  const custodyOn = !!(seat && seat.custodyAllowed);
  const claimOn = !!(seat && seat.claimAllowed);
  const challengeOn = !!(seat && seat.challengeAllowed);
  const mac = String((identify && identify.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  const hwDefault = liveClaimDeviceIdOf(identify, deviceId);
  const claimable = claimableIdentifyOf(identify);
  const registeredId = identify && identify.status === "already_registered" && identify.device_id;
  const claimed = !!(claimOut && (claimOut.ok || claimOut.device_id || claimOut.lifecycle_state));
  const [showLegacy, setShowLegacy] = useState(false);
  const [pem, setPem] = useState("");
  const [sig, setSig] = useState("");
  const [chal, setChal] = useState(null);

  useEffect(() => {
    if (!deviceId && hwDefault) setDeviceId(hwDefault);
  }, [hwDefault, deviceId, setDeviceId]);

  const requestChallenge = async () => {
    if (!custodyOn || !challengeOn) {
      toast && toast("Challenge seat closed", "warn");
      return;
    }
    if (!claimable || mac.length !== 12) {
      toast && toast("Identify a claimable board first", "warn");
      return;
    }
    if (!String(pem || "").includes("BEGIN")) {
      toast && toast("Paste the board public key from the factory pack", "warn");
      return;
    }
    const did = liveClaimDeviceIdOf(identify, deviceId);
    if (!did) {
      toast && toast("Use the sticker serial from the board", "warn");
      return;
    }
    const out = await run(safeActivatePath("post__provision_challenge_request"), {
      body: { mac_nocolon: mac, device_id: did, public_key_pem: pem },
    });
    if (!out) {
      toast && toast(softActivateErrorOf(errorOf()) || "Challenge request refused", "bad");
      return;
    }
    setChal(out);
    toast && toast("Proof request is ready", "go");
  };

  const verifySig = async () => {
    if (!custodyOn || !challengeOn) {
      toast && toast("Challenge seat closed", "warn");
      return;
    }
    if (!chal || chal.challenge_id == null) {
      toast && toast("Request a challenge first", "warn");
      return;
    }
    if (String(sig || "").trim().length < 32) {
      toast && toast("Paste the proof from the board", "warn");
      return;
    }
    const out = await run(safeActivatePath("post__provision_challenge_verify"), {
      body: { challenge_id: Number(chal.challenge_id), signature_b64: String(sig).trim() },
    });
    if (!out) {
      toast && toast(softActivateErrorOf(errorOf()) || "Verify refused", "bad");
      return;
    }
    toast && toast("Signature accepted", "go");
  };

  const doClaim = async () => {
    if (!custodyOn || !claimOn) {
      toast && toast("Claim is closed for this seat", "warn");
      return;
    }
    if (!claimable) {
      toast && toast("Identify a claimable board first", "warn");
      return;
    }
    const did = liveClaimDeviceIdOf(identify, deviceId);
    if (!did || !DEVICE_ID_RE.test(did)) {
      toast && toast("Use the sticker serial from the board", "warn");
      return;
    }
    const body = {
      mac_nocolon: mac,
      device_id: did,
      zone: String(zone || "all").trim() || "all",
    };
    const qr = identify && identify.qr_code;
    if (qr && String(qr).includes("|")) body.qr_code = qr;
    const out = await run(safeActivatePath("post__provision_claim"), { body });
    if (!out) {
      const detail = softActivateErrorOf(errorOf()) || "Claim refused";
      toast && toast(detail, "bad");
      if (challengeOn || /412|challenge/i.test(String(errorOf() || detail))) setShowLegacy(true);
      return;
    }
    setClaimOut(out);
    if (out.device_id) setDeviceId(String(out.device_id));
    if (out.bootstrap_publish_ok === false) {
      toast && toast("Taken · Wi‑Fi setup is still waiting", "warn");
    } else {
      toast && toast("This board is yours · " + (out.device_id || did), "go");
    }
    goStep(2);
  };

  return html`
    <section className="act3__pane">
      <header className="act3__sec-head">
        <span>Take this board</span>
        <em>${claimed ? "done" : claimable ? "ready" : "need find"}</em>
      </header>
      ${envelope
        ? html`<p className="act3__hint-row" data-cmc-last-action="1">Last action · ${envelopeSummary(envelope) || "—"}</p>`
        : null}
      ${!custodyOn && html`<${Alert} tone="warn" title="Closed" say="This seat cannot take a board yet."/>`}
      ${!identify && html`<${Alert} tone="warn" title="No board" say="Scan or type the sticker first."/>`}
      ${identify && !claimable && html`<${Alert} tone="warn" title=${identify.status || "Blocked"}
        say=${identify.message || "Cannot claim."}/>`}
      ${registeredId && html`<div className="act3__acts">
        <${Act3Btn} variant="go"
          onClick=${() => go("/machine/" + encodeURIComponent(String(registeredId)))}>
          Open this board</${Act3Btn}>
      </div>`}
      ${hintsOf(identify).length > 0 && html`
        <ol className="act3__hints act3__hints--inline">
          ${hintsOf(identify).map((h, i) => html`<li key=${i}>${h}</li>`)}
        </ol>`}

      <div className="act3__fields">
        <${Field} label="Board name" hint="Use the sticker serial, or the name this site already uses">
          <${Input} icon="chip" value=${deviceId || hwDefault}
            disabled=${!claimable || claimed}
            onChange=${(e) => setDeviceId(e.target.value)}/>
        <//>
        <${Field} label="Zone" hint="Which area this board belongs to">
          <${Input} icon="map" value=${zone} disabled=${!claimable || claimed}
            onChange=${(e) => setZone(e.target.value)}/>
        <//>
      </div>
      <p className="act3__quiet">From the scan on this board</p>
      ${claimed && claimOut && html`<${M.div} className="act3__pulse" data-tone="go"
        initial=${reduceMotion ? false : { opacity: 0, y: 8, scale: 0.98 }}
        animate=${{ opacity: 1, y: 0, scale: 1 }}
        transition=${ACT3_SPRING}>
        <b>${formatAct3Status(claimOut.lifecycle_state || "claimed")}</b>
        <span>
          ${boardHeadingOf(claimOut.device_id)}
          ${claimOut.owner_admin && !looksLikeCode(claimOut.owner_admin) ? " · " + claimOut.owner_admin : ""}
          ${claimOut.bootstrap_publish_ok === false ? " · Wi‑Fi setup waiting" : ""}
          ${claimOut.bootstrap_publish_ok === true ? " · Wi‑Fi setup queued" : ""}
        </span>
      <//>`}
      <div className="act3__acts">
        <${Act3Btn} variant="go"
          disabled=${busy || !claimable || claimed || !claimOn}
          onClick=${doClaim}>
          ${busy ? "Claiming…" : claimed ? "Claimed" : "Claim board"}</${Act3Btn}>
        <${Act3Btn} variant="sm" hover=${ACT3_HOVER_SUBTLE}
          onClick=${() => setShowLegacy((v) => !v)}>
          ${showLegacy ? "Hide signed proof" : "Signed proof (rare)"}</${Act3Btn}>
      </div>
      ${error && html`<${Alert} tone="bad" title="Refused" say=${softActivateErrorOf(error)}/>`}

      <${AnimatePresence}>
        ${showLegacy && html`
          <${M.div} className="act3__legacy" initial=${{ opacity: 0, height: 0 }}
            animate=${{ opacity: 1, height: "auto" }} exit=${{ opacity: 0, height: 0 }}
            transition=${SOFT}>
            <header className="act3__sec-head">
              <span>Legacy PEM</span>
              <em>${challengeOn ? "optional unless enforce on" : "seat closed"}</em>
            </header>
            <p className="act3__quiet">The usual path is the first Wi‑Fi join. Use this only if the board asks for a signed proof.</p>
            <${Field} label="Board public key">
              <textarea className="inp" rows=${3} value=${pem} onChange=${(e) => setPem(e.target.value)}
                placeholder="-----BEGIN PUBLIC KEY-----"/>
            <//>
            <div className="act3__acts">
              <${Act3Btn} variant="sm" hover=${ACT3_HOVER_SUBTLE}
                disabled=${busy || !custodyOn || !challengeOn}
                onClick=${requestChallenge}>
                ${busy ? "…" : "Request challenge"}</${Act3Btn}>
            </div>
            ${chal && html`<div className="act3__chal">
              <span><u>request</u><code>${chal.challenge_id}</code></span>
              <span><u>code</u><code>${chal.nonce}</code></span>
              ${chal.expires_at_ts != null && html`<span><u>expires</u><code>${chal.expires_at_ts}</code></span>`}
            </div>`}
            <${Field} label="Board signature">
              <${Input} icon="lock" value=${sig} onChange=${(e) => setSig(e.target.value)} placeholder="base64"/>
            <//>
            <div className="act3__acts">
              <${Act3Btn} variant="sm" hover=${ACT3_HOVER_SUBTLE}
                disabled=${busy || !chal || !custodyOn || !challengeOn}
                onClick=${verifySig}>Verify</${Act3Btn}>
            </div>
          <//>`}
      <//>
    </section>`;
}

/* ── Finish ── */
function FinishPane({ deviceId, zone, claimOut, toast, seat, goStep, resetSession, machineId, reduceMotion }) {
  const did = activateMachineIdOf(null, claimOut, machineId || deviceId);
  const wireOn = !!(seat && seat.wireAllowed === true);
  const cred = claimOut && claimOut.cred_version != null ? String(claimOut.cred_version) : "—";
  const { run, busy, error, errorOf, envelope } = useAction();
  const [ssid, setSsid] = useState("");
  const [wifiPass, setWifiPass] = useState("");
  const [taskId, setTaskId] = useState("");
  const taskPath = did && taskId && wireOn
    ? path("get__devices_p_provision_wifi-task_p", { pathParams: [did, taskId] })
    : null;
  const task = useApi(taskPath);
  const pins = useApi(did && wireOn ? path("get__devices_p_pins", { pathParams: [did] }) : null);
  const loopLive = useApi(
    did && wireOn ? (safeBoardPath("get__devices_p_board-loop", { pathParams: [did] }) || null) : null
  );
  const loops = useMemo(() => boardLoopViewOf(loopLive.data), [loopLive.data]);
  const loopFacts = useMemo(() => edgeWaveHonestyOf(loops), [loops]);
  const seats = (pins.data && (pins.data.items || pins.data.pins || pins.data.seats)) || [];
  const [gnssSeal, setGnssSeal] = useState(null);
  const gnssOnce = useRef(false);

  useEffect(() => {
    if (gnssOnce.current) return undefined;
    if (!did || !claimOut) return undefined;
    if (activateLocalUnlock()) return undefined;
    if (!wireOn) return undefined;
    if (loopLive.loading) return undefined;
    if (loopFacts.locateSealed) {
      gnssOnce.current = true;
      setGnssSeal({ ok: false, reason: "already_sealed", st: "already" });
      return undefined;
    }
    gnssOnce.current = true;
    setGnssSeal({ st: "seeking" });
    void postDevicePositionSeal({ run, pathFn: path, deviceId: did, toast }).then((r) => {
      setGnssSeal(r && r.posted ? { ...r, st: "ok" } : { ...(r || {}), st: "hold" });
    });
    return undefined;
  }, [did, claimOut, wireOn, loopLive.loading, loopFacts.locateSealed, run, toast]);

  const retryGnss = async () => {
    if (!wireOn) {
      toast && toast("Wire seat closed", "warn");
      return;
    }
    if (!did) {
      toast && toast("Claim a device first", "warn");
      return;
    }
    setGnssSeal({ st: "seeking" });
    const r = await postDevicePositionSeal({ run, pathFn: path, deviceId: did, toast });
    setGnssSeal(r && r.posted ? { ...r, st: "ok" } : { ...(r || {}), st: "hold" });
  };

  useEffect(() => {
    if (!taskPath) return undefined;
    const id = setInterval(() => { if (task.reload) task.reload(); }, 2500);
    return () => clearInterval(id);
  }, [taskPath, task.reload]);

  const startWifi = async () => {
    if (!wireOn) { toast && toast("Wire seat closed", "warn"); return; }
    if (!did) { toast && toast("Claim a device first", "warn"); return; }
    if (!ssid.trim()) { toast && toast("SSID required", "bad"); return; }
    const out = await run(path("post__devices_p_provision_wifi-task", { pathParams: [did] }), {
      method: "POST",
      body: { ssid: ssid.trim(), password: wifiPass || "", chain: [] },
    });
    if (!out) {
      toast && toast(softActivateErrorOf(errorOf()) || "Wi-Fi task refused", "bad");
      return;
    }
    const tid = String(out.task_id || "").trim();
    if (!tid) {
      toast && toast("Wi‑Fi task returned no id", "bad");
      return;
    }
    setTaskId(tid);
    toast && toast("Wi-Fi task · " + wifiLabel(out.status || "running"), "go");
  };

  const taskRow = (task.data && (task.data.item || task.data)) || null;
  const taskStatus = taskRow && (taskRow.status || taskRow.state);
  const taskMsg = taskRow && taskRow.message;
  const taskProgress = taskRow && taskRow.progress;
  const machineHref = did ? ("/machine/" + encodeURIComponent(did)) : "/machine";

  return html`
    <section className="act3__pane">
      <header className="act3__sec-head">
        <span>Hand off</span>
        <em>${claimOut ? "yours" : "take it first"}</em>
      </header>
      ${envelope
        ? html`<p className="act3__hint-row" data-cmc-last-action="1">Last action · ${envelopeSummary(envelope) || "—"}</p>`
        : null}
      <div className="act3__handoff">
        <b className=${did ? "act3__copy-dd" : ""}
          title=${did ? "Click to copy this board's name" : ""}
          onClick=${did ? () => copyAct3Text(did, toast, "Device id") : undefined}>${did || "—"}</b>
        <span>
          credential v${cred}
          ${claimOut && claimOut.lifecycle_state ? " · " + formatAct3Status(claimOut.lifecycle_state) : ""}
          ${claimOut && claimOut.owner_admin ? " · owner " + claimOut.owner_admin : ""}
          · zone input ${zone || "all"}
          · pins ${!wireOn ? "—" : pins.loading ? "…" : seats.length}
        </span>
      </div>
      ${claimOut && claimOut.bootstrap_publish_ok === false && html`
        <${Alert} tone="warn" title="Wi‑Fi setup waiting"
          say=${claimOut.bootstrap_publish_detail || "This board is on your site. Wi‑Fi setup is not confirmed yet."}/>`}
      <header className="act3__sec-head act3__sec-head--gap">
        <span>Locate</span>
        <em>phone GNSS · Soft≠ cell / GeoIP</em>
      </header>
      ${loopLive.loading && !gnssSeal
        ? html`<p className="act3__quiet">Checking whether this board already has an install seal…</p>`
        : gnssSeal && gnssSeal.st === "seeking"
        ? html`<p className="act3__quiet">Reading this phone’s GNSS · stand at the board · accuracy ≤ 50 m</p>`
        : html`<${Alert}
            tone=${gnssSeal && gnssSeal.st === "ok" ? "go" : (loopFacts.locateSealed ? "go" : "warn")}
            title=${gnssSeal && gnssSeal.st === "ok"
              ? "Sealed from this phone"
              : (loopFacts.locateSealed ? "Already sealed" : "Need GNSS at the board")}
            say=${gnssSeal
              ? gnssSealSay(gnssSeal)
              : "After claim, this phone GNSS (not cell towers) seals the board. The P4 has no GPS chip."}/>`}
      <div className="act3__acts">
        <${Act3Btn} variant="sm-go"
          data-cmc-activate-gnss-ok=${wireOn ? "1" : "0"}
          disabled=${busy || !did || !wireOn} onClick=${retryGnss}>
          ${busy && gnssSeal && gnssSeal.st === "seeking" ? "…" : "Seal from this phone GNSS"}</${Act3Btn}>
      </div>
      <div className="act3__acts">
        <${Act3Btn} variant="go" disabled=${!did}
          onClick=${() => go(machineHref)}>Open this board</${Act3Btn}>
        <${Act3Btn} hover=${ACT3_HOVER_SUBTLE} onClick=${() => {
          resetSession && resetSession();
          toast && toast("Ready for another board", "go");
        }}>Enroll another</${Act3Btn}>
      </div>
      <p className="act3__quiet">Interfaces · capabilities · map bind on Machine (Live).</p>
      ${loops && loops.items.length
        ? html`<div className="machine-hw__loops" data-cmc-board-loop="1">
            ${loops.items.map((x) => html`
              <div key=${x.id} className="machine-hw__loop" data-tone=${x.tone}>
                <b>${x.id}</b>
                <span>${x.value}</span>
              </div>`)}
          </div>`
        : null}

      <header className="act3__sec-head act3__sec-head--gap">
        <span>Wi‑Fi</span>
        <em>optional · Live task</em>
      </header>
      ${!wireOn ? html`<p className="act3__quiet">Setup seat closed.</p>`
        : !did ? html`<p className="act3__quiet">Claim first.</p>`
        : html`<${F}>
            <div className="act3__fields">
              <${Field} label="SSID">
                <${Input} icon="radio" value=${ssid}
                  onChange=${(e) => setSsid(e.target.value)}
                  onKeyDown=${(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      startWifi();
                    }
                  }}
                  placeholder="2.4 GHz"/>
              <//>
              <${Field} label="Password">
                <${Input} icon="lock" type="password" value=${wifiPass}
                  onChange=${(e) => setWifiPass(e.target.value)} autocomplete="new-password"/>
              <//>
            </div>
            <div className="act3__acts">
              <${Act3Btn} variant="sm-go"
                disabled=${busy || !ssid.trim()} onClick=${startWifi}>
                ${busy ? "…" : "Start Wi‑Fi task"}</${Act3Btn}>
            </div>
            ${taskId && html`<${M.div} className="act3__pulse" data-tone=${
              taskStatus === "success" ? "go" : taskStatus === "failed" ? "warn" : "go"
            }
              initial=${reduceMotion ? false : { opacity: 0, y: 6 }}
              animate=${{ opacity: 1, y: 0 }}
              transition=${ACT3_SPRING}>
              <b>${wifiLabel(taskStatus)}</b>
              <span>
                ${taskId}
                ${taskMsg ? " — " + taskMsg : ""}
              </span>
              ${taskProgress != null && html`<div className="act3__wifi-meter" role="progressbar"
                aria-valuenow=${taskProgress} aria-valuemin="0" aria-valuemax="100">
                <i style=${{ width: Math.min(100, Math.max(0, Number(taskProgress) || 0)) + "%" }}/>
              </div>`}
            <//>`}
            ${error && html`<${Alert} tone="bad" title="Refused" say=${softActivateErrorOf(error)}/>`}
          <//>`}
    </section>`;
}
